/**
 * Application Messages Configuration
 * Centralized message strings for consistent responses across the application
 */

export const messages = {
  // Authentication Messages
  auth: {
    unauthenticated: 'Unauthenticated',
    userNotExist: 'User not exist',
    userExist: 'Get user details successfully',
    loginSuccess: 'User logged in successfully!',
    logoutSuccess: 'User logged out successfully!',
    passwordNotMatch: 'Current password does not match.',
    passwordChanged: 'Password changed successfully.',
    passwordNotChange: 'Password not changed.',
    matchWithOldPass: 'Password should not match with old password',
    forceLogoutByAdmin: 'Your session has been terminated by an administrator. Please log in again.',
    forceLogoutConfirmation: 'This will terminate all active sessions and API tokens for this user.',
  },

  // Profile Messages
  profile: {
    completedUserProfile: 'You have successfully completed your profile.',
    profileSuccessfullySaved: 'You have successfully saved your profile.',
    profileUpdated: 'You have successfully saved your profile.',
    profileImageUploadSuccess: 'Profile image uploaded successfully.',
    pleaseCompleteProfileToAddAircraft: 'Please complete your profile before adding an aircraft.',
    firstAircraftSave: 'This aircraft is saved but NOT added. Please edit, enter or review all information and proceed to add aircraft before adding a flight.',
  },

  // 2FA Messages
  twoFactor: {
    enableTwoFactor: '2FA Activation',
    twoFactorCreated: 'You have successfully activated your 2FA authentication',
    twoFactorSaved: 'You have successfully saved your 2FA authentication',
    otpVerifications: 'OTP Verification',
    incorrectAnswer: 'Incorrect answer. Please try again',
    allAnswersRequired: 'All answers are required',
    questionsFetched: 'Security questions fetched successfully',
    questionsNotFetched: 'Security questions not fetched',
  },

  // User Management Messages
  user: {
    userCreated: 'User created successfully',
    userUpdated: 'User updated successfully',
    userDeleted: 'User archived successfully',
    userPerDeleted: 'User permanently deleted successfully',
    userDeletePrevent: "The accounts can't be deleted if they have an active contract.",
    userForcedLogout: 'User has been forced to logout successfully',
    youDoNotHavePermission: 'Access denied. Please contact the account administrator.',
  },

  // SubAdmin Messages
  subAdmin: {
    created: 'SubAdmin user account created successfully. Notification email has been sent to the user',
    updated: 'Permissions updated successfully',
    deleted: 'SubAdmin archived successfully',
    perDeleted: 'SubAdmin permanently deleted successfully',
    restored: 'SubAdmin successfully restored.',
    enabled: 'SubAdmin enabled successfully',
    disabled: 'SubAdmin disabled successfully',
    confirmActive: 'Are you sure you want to activate SubAdmin? Please re-confirm this action.',
    confirmDeactive: 'Are you sure you want to deactivate SubAdmin? Please re-confirm this action.',
  },

  // Airliner Messages
  airliner: {
    created: 'Airliner user account created successfully. Notification email has been sent to the user',
    updated: 'Airliner updated successfully',
    deleted: 'Airliner archived successfully',
    perDeleted: 'Airliner permanently deleted successfully',
    restored: 'Airliner successfully restored.',
    enabled: 'Airliner enabled successfully',
    disabled: 'Airliner disabled successfully',
    confirmActive: 'Are you sure you want to activate Airliner? Please re-confirm this action.',
    confirmDeactive: 'Are you sure you want to deactivate Airliner? Please re-confirm this action.',
  },

  // JetSetter Messages
  jetSetter: {
    created: 'JetSetter user account created successfully. Notification email has been sent to the user',
    updated: 'JetSetter updated successfully',
    deleted: 'JetSetter archived successfully',
    perDeleted: 'JetSetter permanently deleted successfully',
    restored: 'JetSetter successfully restored.',
    enabled: 'JetSetter enabled successfully',
    disabled: 'JetSetter disabled successfully',
    confirmActive: 'Are you sure you want to activate JetSetter? Please re-confirm this action.',
    confirmDeactive: 'Are you sure you want to deactivate JetSetter? Please re-confirm this action.',
  },

  // Aircraft Messages
  aircraft: {
    created: 'Aircraft successfully added',
    saved: 'Aircraft saved successfully',
    createdDraft: 'The aircraft is not added until aircraft image and seating layout are not uploaded',
    updated: 'Aircraft updated successfully.',
    editUpdated: 'Aircraft updated successfully.',
    imageRemoved: 'Image removed successfully',
    deleted: 'Aircraft deleted successfully',
    perDeleted: 'Aircraft permanently deleted successfully',
    restored: 'You have relisted this aircraft successfully',
    imageFetchSuccess: 'Aircraft images fetched successfully',
    noImageAvailable: 'No aircraft image available.',
  },

  // Flight Messages
  flight: {
    created: 'You have added a flight successfully',
    updated: 'Flight updated successfully',
    saved: 'You have saved a flight successfully',
    deleted: 'Flight deleted successfully',
    deletedSuccessful: 'Flight deleted successfully',
    perDeleted: 'Flight permanently deleted successfully',
    restored: 'You have relisted this flight successfully',
    cancelled: 'Flight cancelled successfully',
    cancelSuccessful: 'Flight cancelled successfully',
    rescheduled: 'You have rescheduled a flight successfully',
    published: 'Congratulations, you have successfully published this flight.',
    detailsFetched: 'Flight details fetched successfully.',
    noFlightDetails: 'Flight details not available.',
  },

  // Booking Messages
  booking: {
    details: 'Booking details',
    confirmation: 'Booking details saved successfully',
    flightConfirmation: 'Flight confirmation',
    flightCancellation: 'Flight cancellation',
  },

  // Passenger Messages
  passenger: {
    updated: 'Passenger details updated successfully',
    showDetails: 'Show passenger details',
  },

  // Email Messages
  email: {
    mailSent: 'Mail sent successfully',
    registerSaveEmailVerify: 'You have successfully created your user account. Please check your email to complete the registration.',
    completeProfile: 'Successfully received email to create password',
    clickLinkCompleteProfile: 'Please click the following link to complete your profile.',
    profileCompleted: 'You have successfully created your password, please proceed to log in and complete your user profile.',
    subAdminProfileCompleted: 'You have successfully created your password, please proceed to log in.',
    clickLinkCompleteProfileForSax: 'Profile completed successfully. More information please contact admin.',
    updatePasswordChange: 'You have successfully created your password and verified your profile. For future log-ins, please use your account credentials below.',
    profilePasswordChange: 'You have successfully changed your password. If you have not done so, please contact admin immediately.',
    profilePasswordChangeText: 'If you have done this, no action is necessary. If you have not done this, please contact support immediately.',
    strongPasswordSetLink: 'Your system administrator account (User ID: :attribute) has been created. Please proceed to create your strong password and log in to your account to verify your profile.',
  },

  // CMS Messages
  cms: {
    created: 'New page added successfully',
    updated: 'Page updated successfully',
    deleted: 'Page deleted successfully',
    restored: 'Content page successfully restored.',
    confirmRestore: 'You are about to restore this content page. Click YES to proceed or CANCEL to exit.',
  },

  // Blog Messages
  blog: {
    created: 'New blog added successfully',
  },

  // Contract Messages
  contract: {
    notEditable: 'Selected contract cannot be updated because it is already sent for signature.',
    cancelled: 'Signature request for this contract has been cancelled.',
    updateSend: 'Contract details successfully updated and signature request sent to selected PJOs.',
    updated: 'Contract details successfully updated.',
    deleted: 'Contract details successfully deleted.',
    restored: 'Contract successfully restored.',
    confirmRestore: 'You are about to restore this contract. Click YES to proceed or CANCEL to exit.',
  },

  // General Messages
  general: {
    somethingWentWrong: 'Something went wrong.',
    noDataAvailable: 'No data available',
    registrationSuccessful: 'Registration successful.',
    registrationSuccessfulCreatePass: 'User account invitation & password creation.',
    noCountryDetails: 'No country details available',
    countryDetails: 'Country details fetched',
    nationalityDetails: 'Nationality details fetched',
  },

  // Error Messages
  error: {
    noFile: 'User profile image is null. Please send image.',
    selectTemplate: 'Please select a template stored in HelloSign template list',
  },
};

/**
 * Get a message by key with optional parameter replacement
 * @param key - Message key (e.g., 'auth.loginSuccess')
 * @param params - Optional parameters for string replacement
 * @returns The message string
 */
export const getMessage = (key, params = {}) => {
  const keys = key.split('.');
  let message = messages;

  for (const k of keys) {
    message = message?.[k];
    if (!message) return key; // Return key if message not found
  }

  if (typeof message !== 'string') return key;

  // Replace parameters
  Object.entries(params).forEach(([param, value]) => {
    message = message.replace(`:${param}`, value);
  });

  return message;
};

export default messages;
